import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured, transformContactToCamelCase } from '../utils/supabase';

/**
 * Custom hook for managing contact network relationships
 * 
 * These are general, non-deal-specific relationships like:
 * - "This agent is my referral partner"
 * - "This is my preferred title company"
 * - "This person is in my sphere of influence"
 * 
 * @param {string} contactId - The Supabase UUID of the contact to manage relationships for
 */
export function useContactRelationships(contactId) {
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Transform relationship from snake_case to camelCase
  const transformRelationship = (relationship) => ({
    id: relationship.id,
    relatedContactId: relationship.related_contact_id,
    relationshipType: relationship.relationship_type,
    notes: relationship.notes,
    lastInteractionDate: relationship.last_interaction_date,
    createdAt: relationship.created_at,
    // Include transformed related contact if present
    relatedContact: relationship.crm_contacts 
      ? transformContactToCamelCase(relationship.crm_contacts)
      : null,
  });

  const fetchRelationships = useCallback(async () => {
    if (!contactId || !isSupabaseConfigured()) {
      setRelationships([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('crm_contact_relationships')
        .select(`
          *,
          crm_contacts!related_contact_id(id, first_name, last_name, email, phone, contact_types, company, job_title)
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('[useContactRelationships] Error fetching relationships:', fetchError);
        setError(fetchError.message);
        setRelationships([]);
      } else {
        const transformed = (data || []).map(transformRelationship);
        setRelationships(transformed);
      }
    } catch (err) {
      console.error('[useContactRelationships] Unexpected error:', err);
      setError(err.message);
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchRelationships();

    // Set up real-time subscription
    if (!contactId || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`contact-relationships-${contactId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_contact_relationships',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          fetchRelationships();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contactId, fetchRelationships]);

  // Add relationship (with reciprocal creation)
  const addRelationship = useCallback(async (relatedContactId, relationshipType, notes = '', createReciprocal = true) => {
    if (!contactId) {
      return { success: false, error: 'No contact ID provided' };
    }

    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      // Add relationship to this contact
      const { data: newRelationship, error: insertError } = await supabase
        .from('crm_contact_relationships')
        .insert({
          contact_id: contactId,
          related_contact_id: relatedContactId,
          relationship_type: relationshipType,
          notes: notes || null,
          last_interaction_date: new Date().toISOString(),
        })
        .select(`
          *,
          crm_contacts!related_contact_id(id, first_name, last_name, email, phone, contact_types, company, job_title)
        `)
        .single();

      if (insertError) {
        // Handle unique constraint violation (relationship already exists)
        if (insertError.code === '23505') {
          return { success: false, error: 'This relationship already exists' };
        }
        throw insertError;
      }

      // INSTANT UPDATE: Add to local state immediately
      if (newRelationship) {
        setRelationships(prev => [transformRelationship(newRelationship), ...prev]);
      }

      // Create reciprocal relationship if requested
      if (createReciprocal) {
        const { error: reciprocalError } = await supabase
          .from('crm_contact_relationships')
          .insert({
            contact_id: relatedContactId,
            related_contact_id: contactId,
            relationship_type: relationshipType, // TODO: Use reciprocal type from config
            notes: notes ? `Reciprocal: ${notes}` : null,
            last_interaction_date: new Date().toISOString(),
          });

        // Ignore unique constraint violations for reciprocal (might already exist)
        if (reciprocalError && reciprocalError.code !== '23505') {
          console.error('[useContactRelationships] Error creating reciprocal:', reciprocalError);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('[useContactRelationships] Error adding relationship:', err);
      return { success: false, error: err.message };
    }
  }, [contactId]);

  // Remove relationship
  const removeRelationship = useCallback(async (relationshipId, removeReciprocal = true) => {
    if (!contactId) {
      return { success: false, error: 'No contact ID provided' };
    }

    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    try {
      // Get relationship data before deleting (for reciprocal deletion)
      const { data: relationshipData, error: fetchError } = await supabase
        .from('crm_contact_relationships')
        .select('related_contact_id')
        .eq('id', relationshipId)
        .single();

      if (fetchError) {
        console.error('[useContactRelationships] Error fetching relationship:', fetchError);
        return { success: false, error: 'Relationship not found' };
      }

      const relatedContactId = relationshipData.related_contact_id;

      // INSTANT UPDATE: Remove from local state immediately
      setRelationships(prev => prev.filter(r => r.id !== relationshipId));

      // Delete the relationship
      const { error: deleteError } = await supabase
        .from('crm_contact_relationships')
        .delete()
        .eq('id', relationshipId);

      if (deleteError) {
        console.error('[useContactRelationships] Error deleting relationship:', deleteError);
        // Refetch to restore state on error
        fetchRelationships();
        return { success: false, error: deleteError.message };
      }

      // Delete reciprocal relationship if requested
      if (removeReciprocal && relatedContactId) {
        const { error: reciprocalError } = await supabase
          .from('crm_contact_relationships')
          .delete()
          .eq('contact_id', relatedContactId)
          .eq('related_contact_id', contactId);

        if (reciprocalError) {
          console.error('[useContactRelationships] Error deleting reciprocal:', reciprocalError);
          // Don't fail the overall operation if reciprocal delete fails
        }
      }

      return { success: true };
    } catch (err) {
      console.error('[useContactRelationships] Error removing relationship:', err);
      return { success: false, error: err.message };
    }
  }, [contactId]);

  return {
    relationships,
    loading,
    error,
    addRelationship,
    removeRelationship,
    refetch: fetchRelationships,
  };
}
